import { useState, useEffect, useRef, useCallback } from 'react';

import type { Warning, DeviceCategory } from '../types';

import { warningAPI } from './apiClient';

/**
 * Custom hook for interval-based operations with proper cleanup
 * Prevents memory leaks and unnecessary re-renders
 *
 * @param {Function} callback - Function to execute on interval
 * @param {number} delay - Delay in milliseconds, null to pause
 * @param {boolean} immediate - Run callback immediately on mount
 */
export const useInterval = (
  callback: () => void,
  delay: number | null,
  immediate = false
): void => {
  const savedCallback = useRef<(() => void) | null>(null);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    function tick() {
      savedCallback.current?.();
    }

    // Run immediately if requested
    if (immediate) {
      tick();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    return undefined;
  }, [delay, immediate]);
};

/**
 * Custom hook for redraw counter - optimized for map components
 * Provides a counter that increments on interval for triggering re-renders
 *
 * @param {number|null} intervalMs - Interval in milliseconds, null to pause (default: 30000)
 * @returns {number} Current redraw counter
 */
export const useRedrawCounter = (intervalMs: number | null = 30000): number => {
  const [redraw, setRedraw] = useState(0);

  useInterval(
    useCallback(() => {
      setRedraw((prev) => prev + 1);
    }, []),
    intervalMs // Can be null to pause the counter
  );

  return redraw;
};

/**
 * Custom hook for timeout-based operations with automatic cleanup
 *
 * @param {Function} callback - Function to execute after timeout
 * @param {number} delay - Delay in milliseconds
 * @param {Array} deps - Dependencies that reset the timer
 */
export const useTimeout = (
  callback: () => void,
  delay: number | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: any[] = []
): void => {
  const savedCallback = useRef<(() => void) | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return undefined;

    const id = setTimeout(() => savedCallback.current?.(), delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, ...deps]);
};

/**
 * Custom hook for debounced values
 * Useful for search inputs and expensive operations
 *
 * @param {any} value - Value to debounce
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {any} Debounced value
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for previous value tracking
 * Useful for comparing current vs previous props/state
 *
 * @param {any} value - Value to track
 * @returns {any} Previous value
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

/**
 * Options for useWarnings hook
 */
interface UseWarningsOptions {
  /** Device category to filter warnings */
  category?: DeviceCategory;
  /** Refresh interval in milliseconds (default: 30000) */
  refreshInterval?: number;
}

/**
 * Return type for useWarnings hook
 */
interface UseWarningsResult {
  /** List of warnings */
  warnings: Warning[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Function to manually refresh warnings */
  refresh: () => Promise<void>;
  /** Get warnings for a specific device */
  getWarningsForDevice: (deviceId: string) => Warning[];
}

/**
 * Custom hook for fetching and managing maintenance warnings
 * Consolidates warning fetch logic from DeviceView, BeaconView, and CheckinView
 *
 * @param {UseWarningsOptions} options - Hook options
 * @returns {UseWarningsResult} Warnings state and utilities
 *
 * @example
 * const { warnings, getWarningsForDevice } = useWarnings({ category: 'lf_device' });
 * const deviceWarnings = getWarningsForDevice(device.id);
 */
export const useWarnings = (options: UseWarningsOptions = {}): UseWarningsResult => {
  const { category, refreshInterval = 30000 } = options;
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWarnings = useCallback(async (): Promise<void> => {
    try {
      const token = window.localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const data = await warningAPI.getWarnings({
        status: 'active',
        device_status: 'active',
        ...(category ? { category } : {}),
      });

      if (data.success && data.warnings) {
        setWarnings(data.warnings);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching warnings:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [category]);

  // Initial fetch and interval refresh
  useEffect(() => {
    fetchWarnings();
    const interval = setInterval(fetchWarnings, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchWarnings, refreshInterval]);

  const getWarningsForDevice = useCallback(
    (deviceId: string): Warning[] => {
      return warnings.filter((w) => w.device === deviceId);
    },
    [warnings]
  );

  return {
    warnings,
    isLoading,
    error,
    refresh: fetchWarnings,
    getWarningsForDevice,
  };
};
